import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaCalendarAlt, FaUser, FaEye, FaStar } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import { error as logError } from '../utils/logger';
import MoreNewsCarousel from './MoreNewsCarousel';
import PublicPageLayout from './PublicPageLayout';
import '../styles/Blog.css';
import { apiUrl, authUrl, assetUrl } from '../utils/apiUrl';


const featuredImageUrl = (url) => assetUrl(url);

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const currentLanguage = language || 'es';
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const viewCountedRef = useRef(false);

  useEffect(() => {
    if (slug) {
      loadPost();
    }
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    fetch(authUrl('/auth/me'), { credentials: 'include' })
      .then((r) => { if (r.ok) setIsLoggedIn(true); })
      .catch(() => {});
  }, [post]);

  const loadPost = async () => {
    if (!slug) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(apiUrl(`/api/blog/posts/slug/${encodeURIComponent(slug)}`), {
        credentials: 'include'
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          setError(t('common.blog.postNotFound', currentLanguage));
        } else {
          setError(t('common.blog.errorLoading', currentLanguage));
        }
        return;
      }
      
      const data = await response.json();
      setPost(data);
      updateMetaTags(data);

      // Registrar vista una sola vez por carga de página
      if (!viewCountedRef.current) {
        viewCountedRef.current = true;
        fetch(apiUrl(`/api/blog/posts/slug/${encodeURIComponent(slug)}/view`), {
          method: 'POST',
          credentials: 'include'
        }).then(() => {
          if (data.views !== undefined) setPost((p) => p ? { ...p, views: (p.views || 0) + 1 } : p);
        }).catch(() => {});
      }
    } catch (err) {
      logError('Error cargando post:', err);
      setError(t('common.blog.errorLoading', currentLanguage));
    } finally {
      setLoading(false);
    }
  };

  const updateMetaTags = (postData) => {
    // Actualizar título
    document.title = postData.meta_title || postData.title;
    
    // Actualizar meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', postData.meta_description || postData.excerpt || postData.title);
    
    // Actualizar Open Graph
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', postData.meta_title || postData.title);
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', postData.meta_description || postData.excerpt || postData.title);
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', window.location.href);
    
    if (postData.featured_image) {
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) ogImage.setAttribute('content', featuredImageUrl(postData.featured_image));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      movie: currentLanguage === 'en' ? 'Movies' : 'Películas',
      series: currentLanguage === 'en' ? 'TV Series' : 'Series',
      music: currentLanguage === 'en' ? 'Music' : 'Música'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      movie: 'primary',
      series: 'success',
      music: 'warning'
    };
    return colors[category] || 'secondary';
  };

  const getRateLinkLabel = (category) => {
    if (category === 'movie') return t('common.blog.rateMovie', currentLanguage);
    if (category === 'series') return t('common.blog.rateSeries', currentLanguage);
    if (category === 'music') return t('common.blog.rateMusic', currentLanguage);
    return t('common.blog.rateThisContent', currentLanguage);
  };

  const getRateLinkUrl = () => {
    const params = new URLSearchParams();
    if (post?.category) params.set('category', post.category);
    const searchTerm = (post?.related_title || post?.title || '').trim();
    if (searchTerm) params.set('search', searchTerm);
    const q = params.toString();
    return `/app${q ? `?${q}` : ''}#recommendations`;
  };

  if (loading) {
    return (
      <PublicPageLayout hideHeader backTo="/" backLabel={t('common.blog.backToHome', currentLanguage)}>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">{t('common.blog.loading', currentLanguage)}</p>
        </div>
      </PublicPageLayout>
    );
  }

  if (error || !post) {
    return (
      <PublicPageLayout hideHeader backTo="/" backLabel={t('common.blog.backToHome', currentLanguage)}>
        <Alert variant="danger">
          <Alert.Heading>{t('common.blog.error', currentLanguage)}</Alert.Heading>
          <p>{error || t('common.blog.postNotFound', currentLanguage)}</p>
        </Alert>
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout hideHeader backTo="/" backLabel={t('common.blog.backToHome', currentLanguage)}>
      <article className="blog-article blog-post-page">
        {/* Hero con imagen destacada */}
        {post.featured_image && (
          <div className="blog-hero-image">
            <img src={featuredImageUrl(post.featured_image)} alt={post.title} />
            <div className="blog-hero-overlay" />
          </div>
        )}

        <Container className="blog-article-container">
          <div className="blog-article-meta">
            <Badge bg={getCategoryColor(post.category)} className="blog-category-badge">
              {getCategoryLabel(post.category)}
            </Badge>
            <h1 className="blog-article-title">{post.title}</h1>
            <div className="blog-article-byline">
              {post.author_name && (
                <span className="blog-byline-item">
                  <FaUser className="me-1" />
                  {post.author_name}
                </span>
              )}
              {post.published_at && (
                <span className="blog-byline-item">
                  <FaCalendarAlt className="me-1" />
                  {formatDate(post.published_at)}
                </span>
              )}
              {post.views > 0 && (
                <span className="blog-byline-item">
                  <FaEye className="me-1" />
                  {post.views} {t('common.blog.views', currentLanguage)}
                </span>
              )}
            </div>
            {post.excerpt && (
              <p className="blog-article-lead">{post.excerpt}</p>
            )}
            {isLoggedIn && (
              <div className="blog-rate-cta mt-3">
                <Link to={getRateLinkUrl()} className="btn btn-outline-primary btn-sm">
                  <FaStar className="me-2" />
                  {getRateLinkLabel(post.category)}
                </Link>
              </div>
            )}
          </div>

          <div
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </Container>
      </article>

      <MoreNewsCarousel excludePostId={post.id} limit={10} />
    </PublicPageLayout>
  );
};

export default BlogPost;
